import { useQuery } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { queries } from "../../shared/queries";
import { useNavTransition } from "../hooks/useNavTransition";
import { usePersonalWorkEnabled } from "../hooks/usePersonalWorkEnabled";
import { isAdminViewUrl } from "../lib/adminViewUrls";
import {
  isRouteLocation,
  lastVisitedLocation,
  routes,
  useCurrentLocation,
} from "../lib/urls";
import { BadgeDot } from "./BadgeDot";
import { isInAppNavigation, Link } from "./Link";
import { ButtonContent } from "./Button/ButtonContent";
import { Icon } from "./Icon";
import { Tooltip } from "./Tooltip";

type Mode = "Chat" | "Work";

function modeForLocation(location: string): Mode | null {
  if (isAdminViewUrl(location)) return null;
  const [pathname = "", search] = location.split("?");
  if (pathname === "/chats") return "Chat";
  if (
    isRouteLocation(routes.space.workflow, pathname) &&
    new URLSearchParams(search).has("chat")
  )
    return "Chat";
  return /^\/(recents|favorites|monitoring|links|connectors|skills|personal|spaces)(\/|\?|$)/.test(
    location
  )
    ? "Work"
    : null;
}

function isWorkSectionLocation(location: string): boolean {
  if (modeForLocation(location) !== "Work") return false;
  const [pathname = ""] = location.split("?");
  const segments = pathname.split("/").filter(Boolean);
  // Space tabs are one level below a space; workflow and creation pages are
  // deeper than the list surfaces the Work switch should return to.
  const maxSegments =
    segments[0] === "spaces" ? 3 : segments[0] === "links" ? 1 : 2;
  return segments.length <= maxSegments;
}

function parentWorkLocation(location: string): string | null {
  if (modeForLocation(location) !== "Work") return null;
  const [pathname = ""] = location.split("?");
  const [section, child] = pathname.split("/").filter(Boolean);
  if (section === "spaces") return child ? `/spaces/${child}` : null;
  if (section === "connectors")
    return child ? `/connectors/${child}` : "/connectors";
  return section ? `/${section}` : null;
}

function workspaceModeDestination(mode: Mode, current: string) {
  const location =
    mode === "Chat"
      ? modeForLocation(current) === "Chat"
        ? current
        : (lastVisitedLocation((url) => modeForLocation(url) === "Chat") ??
          "/chats")
      : isWorkSectionLocation(current)
        ? current
        : (lastVisitedLocation(isWorkSectionLocation) ??
          parentWorkLocation(
            lastVisitedLocation((url) => modeForLocation(url) === "Work") ??
              current
          ) ??
          "/recents");
  const [pathname, search] = location.split("?");
  const params = new URLSearchParams(search);
  for (const key of [...params.keys()]) {
    if (key === "modal" || key.startsWith("modal[") || key === "history")
      params.delete(key);
  }
  return `${pathname}${params.size ? `?${params}` : ""}`;
}

const Switch = styled("nav")`
  --tab-hover-growth: 0px;
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  flex: 1;
  min-width: 0;
  border-radius: 999px;
  background: var(--alpha4);
  &:has([aria-current="false"]:hover) {
    --tab-hover-growth: 2px;
  }
`;

const slideToChat = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

const slideToHome = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const Selection = styled("span")`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 50%;
  border-radius: 999px;
  pointer-events: none;
  transform: translateX(0);
  &::before {
    content: "";
    position: absolute;
    inset: 2px;
    right: calc(2px - var(--tab-hover-growth));
    border-radius: inherit;
    color-scheme: light;
    background: var(--surface-primary);
    outline: 0.5px solid var(--border-alpha);
    box-shadow: var(--elevation-low);
    transition:
      left var(--motion-base) var(--ease-out),
      right var(--motion-base) var(--ease-out);
  }
  &[data-mode="Chat"] {
    transform: translateX(100%);
  }
  &[data-mode="Chat"]::before {
    left: calc(2px - var(--tab-hover-growth));
    right: 2px;
  }
  &[data-direction="right"] {
    animation: ${slideToChat} var(--motion-base) var(--ease-out);
  }
  &[data-direction="left"] {
    animation: ${slideToHome} var(--motion-base) var(--ease-out);
  }
`;

const modeStyles = `
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  height: 32px;
  border-radius: 999px;
  text-decoration: none;
  color: var(--text-secondary);
  &:hover {
    color: var(--text-primary);
  }
  &[aria-current="page"] {
    color-scheme: light;
    color: var(--text-primary);
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
  }
`;

const ModeLink = styled(Link)`
  ${modeStyles}
`;

const DisabledMode = styled("span")`
  ${modeStyles}
  cursor: not-allowed;
  > * {
    opacity: 0.5;
  }
`;

const ModeLabel = styled("span")`
  position: relative;
  display: inline-flex;
  transform: translateY(0.5px);
`;

// Sits beside the label so showing it doesn’t shift the word.
const ReadyDot = styled(BadgeDot)`
  position: absolute;
  top: 50%;
  left: calc(100% + 6px);
  transform: translateY(-50%);
`;

export function WorkspaceModeSwitch({
  onActiveModePress,
  navigationOpen,
}: {
  onActiveModePress?: () => void;
  navigationOpen?: boolean;
}) {
  const location = useCurrentLocation();
  const personalWorkEnabled = usePersonalWorkEnabled() === true;
  const currentMode = modeForLocation(location);
  const activeMode =
    currentMode === "Chat" && personalWorkEnabled ? "Chat" : "Work";
  const navEnter = useNavTransition(
    activeMode === "Chat" ? "chat" : "workspace",
    "mode-switch"
  );
  const direction =
    navEnter === "left" ? "right" : navEnter === "right" ? "left" : undefined;
  const [chatTabs] = useQuery(queries.personalChatTabs());
  const chatReady =
    activeMode !== "Chat" &&
    chatTabs.some(
      (tab) => tab.chat?.awaitingAction && tab.chat.respondingAt == null
    );
  return (
    <Switch aria-label="Workspace">
      <Selection
        aria-hidden="true"
        data-mode={activeMode}
        data-direction={direction}
      />
      {(["Work", "Chat"] as const).map((mode) =>
        mode === "Chat" && !personalWorkEnabled ? (
          <Tooltip
            key={mode}
            content="Personal work is disabled"
            fill="stretch"
          >
            <DisabledMode role="link" aria-disabled="true" aria-current={false}>
              <ModeLabel>
                <ButtonContent size="l">Chat</ButtonContent>
              </ModeLabel>
            </DisabledMode>
          </Tooltip>
        ) : (
          <ModeLink
            key={mode}
            to={workspaceModeDestination(mode, location)}
            aria-current={activeMode === mode ? "page" : false}
            aria-expanded={
              currentMode === mode && onActiveModePress
                ? navigationOpen
                : undefined
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") event.stopPropagation();
            }}
            onMouseDown={(event) => {
              if (
                currentMode === mode &&
                onActiveModePress &&
                isInAppNavigation(event)
              ) {
                event.preventDefault();
              }
            }}
            onClick={(event) => {
              if (
                currentMode === mode &&
                onActiveModePress &&
                isInAppNavigation(event)
              ) {
                event.preventDefault();
                onActiveModePress();
              }
            }}
          >
            {mode === "Work" && <Icon name="tines-mark" size={12} />}
            <ModeLabel>
              <ButtonContent size="l">
                {mode === "Work" ? "Home" : mode}
              </ButtonContent>
              {mode === "Chat" && chatReady && (
                <ReadyDot role="status" aria-label="A chat is ready" />
              )}
            </ModeLabel>
          </ModeLink>
        )
      )}
    </Switch>
  );
}
